<template>
  <div class="tag-list flex text-12 text-primary flex-wrap gap-[10px]">
    <a
      v-for="(item, idx) in list"
      :key="idx"
      class="flex-center text-12 bg-back-3 dark:text-font-1 dark:bg-purple-1 hover:bg-back-5 dark:hover:text-font-1 dark:hover:bg-purple-1 rounded-[6px] px-[8px] h-[26px] font-semibold"
      :href="getLink(item)"
      @click="(e) => handleClick(e, item)"
    >
      {{ item.tag_name }}
    </a>
    {{ (list?.length || 0) > 15 ? '...' : '' }}
  </div>
</template>

<script lang="ts" setup>
import { getTagIDs } from '..'
import type { NewTagNode, Template } from '~/api/interface'
import { PageKey } from '~/constants/page'
import { encodeTagUrlPath } from '~/utils/algorithm'

const props = defineProps<{
  new_tag_ids: Record<number, NewTagNode>
  template?: Template
}>()

function flattenNode(
  nodeMap: Record<number, NewTagNode>,
  fullMap: Record<number, NewTagNode>
) {
  for (const key in nodeMap) {
    const node = nodeMap[key]
    fullMap[key] = node
    const pNode = node.tag_pid ? fullMap[node.tag_pid] : null
    node.tag_ids = `${node.level},${node.tag_id}`
    if (pNode) {
      if (node.level <= 2) {
        node.tag_ids = `${pNode.tag_ids}:${node.tag_ids}`
      } else {
        node.tag_ids = pNode.tag_ids
      }
    }
    if (node.children) {
      flattenNode(node.children, fullMap)
    }
  }
}

const list = computed(() => {
  const fullMap: Record<number, NewTagNode> = {}
  flattenNode(props.new_tag_ids, fullMap)
  return Object.values(fullMap)
    .filter((v) => v.detail_is_show && v.level > 0)
    .slice(0, 16)
})

const resourceLocation = injectResourceLocation()

const router = useRouter()

const handleClick = (evt: Event, item: Partial<NewTagNode>) => {
  evt.preventDefault()

  if (!isChinese()) {
    return
  }

  const reportData = props.template
    ? {
        template_id: props.template.id,
        template_title: props.template.name,
        tag_id: item.tag_id,
        tag_name: item.tag_name,
        source: resourceLocation?.source,
        tags: getTagIDs(props.template.tag_ids)
          .map((item) => item.tag_name)
          .join(','),
      }
    : {
        tag_id: item.tag_id,
        tag_name: item.tag_name,
        source: resourceLocation?.source,
      }

  trackEvent(TRACK_EVENT.CLICK_POPUP_TEMPLATE_TAG, reportData)

  const query = {}
  if (item.tag_ids) {
    Object.assign(query, { tag_ids: item.tag_ids })
  }

  router.push({
    name: PageKey.TEMPLATE,
    query,
  })

  const open = useTemplateVisible()
  open.value = false
}

const getLink = (item: Partial<NewTagNode>) => {
  if (!item.tag_ids) {
    return
  }
  return `/tags/${encodeTagUrlPath(item.tag_ids)}.html`
}
</script>
